/**
 * Gated HITL bridge for `apply_asymmetric_margin` (the third mutating
 * path).
 *
 * Flow: parse the model's tool input → `resolveAsymmetricMargin` →
 * `previewAsymmetricMargin` → an {@link AsymMarginProposal} the card renders.
 * The shared gate (≤1-pending preamble, resolve-failure mapping,
 * Apply/Reject wiring) lives in {@link mutatingToolRunner}; this file holds only
 * what's specific to an asymmetric margin — the six required signed directions,
 * the per-direction grid-rounding, and the proposal's display fields. The six
 * explicit signed cm directions are carried verbatim onto the proposal (no
 * inferred symmetry); the patient→engine-axis mapping stays encapsulated in
 * the safety core. Pure/React-free.
 */
import {
    resolveAsymmetricMargin,
    previewAsymmetricMargin,
    commitAsymmetricMargin,
    ASYM_DIRECTION_KEYS,
    type AsymMarginDeps,
    type AsymMarginInput,
} from './pipeline';
import {
    asRecord,
    failedPlan,
    gateProposal,
    mutatingPreamble,
    quantizeCmTracked,
    resolveFailureToPlan,
    type MutatingPlan,
    type MutatingToolCall,
    type MutationOutcome,
    type PendingFlag,
} from '../core';

/** One direction that was rounded to the grid, for the card's disclosure. */
export interface RoundedDirection {
    /** Display label, e.g. "Superior". */
    readonly label: string;
    /** The clinician's original, finer-than-grid request (signed cm). */
    readonly requestedCm: number;
}

export type AsymMarginToolCall = MutatingToolCall;

export interface AsymMarginPlanDeps extends AsymMarginDeps, PendingFlag {}

export interface AsymMarginProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    /** The six explicit signed cm offsets, patient (LPS) frame — carried
     *  verbatim for the card; no direction inferred from another. */
    readonly superiorCm: number;
    readonly inferiorCm: number;
    readonly anteriorCm: number;
    readonly posteriorCm: number;
    readonly leftCm: number;
    readonly rightCm: number;
    /** Directions rounded to the grid, set only when any were finer
     *  (disclosure). Empty/absent when every direction was on-grid. */
    readonly roundedDirections?: ReadonlyArray<RoundedDirection>;
    readonly newRoi: string;
    /** New minus source volume (cm³) — for model chaining. */
    readonly volumeDeltaCm3: number;
    /** Commit the ALREADY-previewed geometry into a new structure. */
    apply(): Promise<MutationOutcome>;
    /** The clinician declined; the model is told and does not assume success. */
    reject(): MutationOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type AsymMarginPlan = MutatingPlan<AsymMarginProposal>;

/** Defensively read the model-supplied (untyped) tool input. ALL six
 *  directions are required — no optional fields, no inferred symmetry. */
function readInput(input: unknown): AsymMarginInput | undefined {
    const o = asRecord(input);
    if (o === undefined) {
        return undefined;
    }
    if (typeof o.roi !== 'string' || typeof o.newRoi !== 'string') {
        return undefined;
    }
    // Single source for the six required directions (ASYM_DIRECTION_KEYS) so
    // the parser cannot drift from the validator if a field is renamed.
    const directions = {} as Record<
        (typeof ASYM_DIRECTION_KEYS)[number],
        number
    >;
    for (const k of ASYM_DIRECTION_KEYS) {
        const value = o[k];
        if (typeof value !== 'number') {
            return undefined;
        }
        directions[k] = value;
    }
    return { roi: o.roi, ...directions, newRoi: o.newRoi };
}

export async function planAsymMarginToolCall(
    call: AsymMarginToolCall,
    deps: AsymMarginPlanDeps
): Promise<AsymMarginPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(
            call.toolCallId,
            'invalid apply_asymmetric_margin input'
        );
    }

    // Round every direction to the clinical grid (0.1 mm) — sub-grid precision
    // is below the achievable voxel resolution. The grid values are what get
    // previewed and applied, so display and geometry agree; finer originals
    // are kept per-direction so the card discloses the rounding (CLAUDE.md:
    // never silently clamp). Label is derived from the key ("superiorCm" →
    // "Superior") so the wording cannot drift from ASYM_DIRECTION_KEYS.
    const quantizedDirections = {} as Record<
        (typeof ASYM_DIRECTION_KEYS)[number],
        number
    >;
    const roundedDirections: RoundedDirection[] = [];
    for (const k of ASYM_DIRECTION_KEYS) {
        const { cm, requestedCm } = quantizeCmTracked(parsed[k]);
        quantizedDirections[k] = cm;
        if (requestedCm !== undefined) {
            const base = k.slice(0, -'Cm'.length);
            roundedDirections.push({
                label: base.charAt(0).toUpperCase() + base.slice(1),
                requestedCm,
            });
        }
    }
    const quantized: AsymMarginInput = {
        roi: parsed.roi,
        ...quantizedDirections,
        newRoi: parsed.newRoi,
    };

    const resolved = resolveAsymmetricMargin(quantized, deps);
    if (resolved.kind !== 'validated') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }

    const preview = await previewAsymmetricMargin(resolved.validated, deps);
    if (preview.kind === 'failed') {
        return failedPlan(call.toolCallId, preview.reason);
    }

    const previewed = preview.previewed;
    const proposal: AsymMarginProposal = {
        toolCallId: call.toolCallId,
        target: {
            roiNumber: resolved.validated.sourceRoiNumber,
            name: parsed.roi,
            currentVolumeCm3: resolved.validated.sourceVolumeCm3,
        },
        superiorCm: quantized.superiorCm,
        inferiorCm: quantized.inferiorCm,
        anteriorCm: quantized.anteriorCm,
        posteriorCm: quantized.posteriorCm,
        leftCm: quantized.leftCm,
        rightCm: quantized.rightCm,
        ...(roundedDirections.length > 0 ? { roundedDirections } : {}),
        newRoi: parsed.newRoi,
        volumeDeltaCm3: previewed.volumeDeltaCm3,
        ...gateProposal(previewed.computed, deps, () =>
            commitAsymmetricMargin(previewed, deps)
        ),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
